import React, { useState, useEffect } from 'react';
import localService from '../../../services/localService';
import clientService from '../../../services/clientService';
import { useTheme } from '../../../contexts/ThemeContext';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';

const EditLocalModal = ({ isOpen, onClose, onSuccess, local }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    adresse: '',
    responsable: '',
    telResponsable: '',
    isDefault: false,
    clientId: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Initialize form data when local changes
  useEffect(() => {
    if (local) {
      setFormData({
        adresse: local.adresse || '',
        responsable: local.responsable || '',
        telResponsable: local.telResponsable || '',
        isDefault: local.isDefault || false,
        clientId: local.clientId?.toString() || ''
      });
    }
  }, [local]);

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const result = await clientService.getClients(100, 1);
      if (result.success && result.data?.list) {
        setClients(result.data.list);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.adresse.trim()) {
        throw new Error('L\'adresse est obligatoire');
      }
      if (!formData.clientId) {
        throw new Error('Veuillez sélectionner un client');
      }

      // Prepare payload
      const payload = {
        id: local.id,
        adresse: formData.adresse.trim(),
        responsable: formData.responsable.trim() || null,
        telResponsable: formData.telResponsable.trim() || null,
        isDefault: formData.isDefault,
        clientId: parseInt(formData.clientId, 10),
        client: null // Will be populated by the API
      };

      const result = await localService.updateLocal(local.id, payload);
      
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.message || 'Erreur lors de la modification du local');
      }
    } catch (error) {
      console.error('Update local error:', error);
      setError(error.message || 'Erreur lors de la modification du local');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen || !local) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-10 h-10 ${isDarkMode ? 'bg-gray-700' : 'bg-white bg-opacity-20'} rounded-xl flex items-center justify-center mr-3`}>
                <i className="fas fa-edit text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Modifier le Local</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>ID: {local.id}</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {error && (
              <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur de validation</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Client Selection */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'p-2 bg-gray-800' : 'p-2 bg-blue-100'} rounded-lg mr-3`}>
                    <i className={`fas fa-user ${isDarkMode ? 'text-gray-300' : 'text-blue-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Client</h3>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-building mr-2 text-gray-400"></i>
                    Client *
                  </label>
                  <ClientSelectDropdown
                    value={formData.clientId}
                    onChange={val => setFormData(prev => ({ ...prev, clientId: val }))}
                    options={clients}
                    loading={loadingClients}
                    disabled={loadingClients}
                    isDarkMode={isDarkMode}
                    placeholder="Sélectionner un client"
                    required={true}
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'p-2 bg-gray-800' : 'p-2 bg-green-100'} rounded-lg mr-3`}>
                    <i className={`fas fa-map-marker-alt ${isDarkMode ? 'text-gray-300' : 'text-green-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations du local</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-location-arrow mr-2 text-gray-400"></i>
                      Adresse *
                    </label>
                    <input 
                      type="text" 
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      placeholder="Ex: Rte tunis km 15 sidi salah sfax"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-user-tie mr-2 text-gray-400"></i>
                      Responsable
                    </label>
                    <input 
                      type="text" 
                      name="responsable"
                      value={formData.responsable}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      placeholder="Ex: Mohamed Ali Gargouri - Ghazi Ayedi"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-phone mr-2 text-gray-400"></i>
                      Téléphone du responsable
                    </label>
                    <input 
                      type="tel" 
                      name="telResponsable"
                      value={formData.telResponsable}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      placeholder="Ex: 58525778 - 53573443"
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-amber-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'p-2 bg-gray-800' : 'p-2 bg-amber-100'} rounded-lg mr-3`}>
                    <i className={`fas fa-star ${isDarkMode ? 'text-gray-300' : 'text-amber-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Statut</h3>
                </div>
                
                <div className={`flex items-center p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                  <input 
                    type="checkbox" 
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleChange}
                    className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                  />
                  <label className={`text-sm font-medium flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-star mr-2 text-gray-400"></i>
                    Local principal
                    <span className="ml-2 text-xs text-gray-500">(Site principal du client)</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg transition-colors font-medium ${isDarkMode ? 'text-gray-200 bg-gray-800 border border-gray-600 hover:bg-gray-700' : 'text-gray-600 bg-white border border-gray-300 hover:bg-gray-50'}`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button 
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                    ? 'bg-primary-700 hover:bg-primary-800 text-white shadow-lg hover:shadow-xl'
                    : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Modification...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Sauvegarder les modifications
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLocalModal;